import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { ComponentPropsWithoutRef, ElementRef, ReactNode, forwardRef, useState } from 'react';
import { cn } from 'styles';

const TooltipProvider = TooltipPrimitive.Provider;

const TooltipRoot = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipPortal = TooltipPrimitive.Portal;

export const TooltipArrow = TooltipPrimitive.Arrow;

type ContentProps = ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>;

export interface TooltipProps {
  /**
   * The tooltip wrapper classname
   */
  className?: string;

  /**
   * Content that has the tooltip
   */
  children: ReactNode;

  /**
   * Whats displayed on the tooltip
   */
  content: ReactNode;

  /**
   * The delay to show up
   */
  delayMilSeconds?: number;

  /**
   * Ensures we have a node capable of handling the events (hover etc)
   *
   * WARNING: This wraps your *children* with a <div />
   */
  ensurePresence?: boolean;

  /**
   * The position of the content.
   *
   * Adjusts automatically if not enough space is present
   */
  position?: ContentProps['side'];

  /**
   * If disabled, just your children renders
   */
  disabled?: boolean;

  /**
   * Opt out of the default styling of our app
   */
  unstyled?: boolean;

  /**
   * Adds the arrow if `true`
   *
   * You can also pass in the <TooltipArrow /> to customize it
   */
  arrow?: boolean | ReactNode;

  /**
   * Enables your root element to be clickable
   *
   * **WARNING** You are still responsible for the trigger to properly receive refs
   */
  clickable?: boolean;

  /**
   * Offset of the content with its trigger
   */
  contentOffset?: number;

  /**
   * Content spacing
   */
  spacing?: ContentSpacing;
}

const spacingClasses = {
  large: 'p-2',
  medium: 'px-3 py-1.5',
  small: 'p-1',
};

type ContentSpacing = keyof typeof spacingClasses;

const TooltipContent = forwardRef<
  ElementRef<typeof TooltipPrimitive.Content>,
  ContentProps & Pick<TooltipProps, 'arrow' | 'unstyled' | 'position' | 'contentOffset' | 'spacing'>
>(
  (
    {
      className,
      contentOffset = 4,
      children,
      arrow,
      unstyled,
      position,
      spacing = 'medium',
      ...props
    },
    ref,
  ) => {
    return (
      <TooltipPortal>
        <TooltipPrimitive.Content
          ref={ref}
          sideOffset={contentOffset}
          side={position}
          {...props}
          className={cn(
            !unstyled &&
              `rounded-md border bg-popover ${spacingClasses[spacing]} text-sm text-popover-foreground`,

            'z-50 max-w-xs overflow-hidden shadow-md animate-in fade-in-0 zoom-in-95',

            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',

            className,
          )}
        >
          {children}

          {/* TODO: Revise looks. Seems off */}
          {typeof arrow === 'boolean' && arrow ? <TooltipArrow className="fill-popover" /> : arrow}
        </TooltipPrimitive.Content>
      </TooltipPortal>
    );
  },
);

TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export function Tooltip({
  children,
  content,
  delayMilSeconds = 250,
  ensurePresence,
  disabled,
  clickable,
  ...props
}: TooltipProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);

  if (disabled) return <>{children}</>;

  const actualChildren = ensurePresence ? (
    <div className="inline-flex w-fit items-center">{children}</div>
  ) : (
    children
  );

  return (
    <TooltipProvider delayDuration={delayMilSeconds}>
      <TooltipRoot open={clickable ? isOpen : undefined} onOpenChange={(open) => setIsOpen(open)}>
        <TooltipTrigger
          asChild
          onClick={
            clickable
              ? (e) => {
                  e.stopPropagation();
                  setIsOpen((prev) => !prev);
                }
              : undefined
          }
        >
          {actualChildren}
        </TooltipTrigger>

        <TooltipContent {...props}>{content}</TooltipContent>
      </TooltipRoot>
    </TooltipProvider>
  );
}
